import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Grid,
  Typography,
  FormControlLabel,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { SnackbarProvider } from 'notistack';
import { CustomInput, CustomSelect, CustomSwitch, Toaster } from '@/components';
import { ActionPopUp } from '@/Layouts';
import { Rules } from '@/helpers/RHFRules';
import {
  createPaymentMethodService,
  updatePaymentMethodService,
} from '@/services';
import type { PaymentMethod } from '../PaymentMethodsScreen';

const useStyles = makeStyles((theme) => ({
  formControl: {
    width: '100%',
  },
  switchContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  switchLabel: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  modalWarning: {
    '& .MuiDialogTitle-root': {
      paddingBottom: theme.spacing(1),
    },
  },
}));

interface PaymentMethodFormProps {
  open: boolean;
  title: string;
  data: PaymentMethod | null;
  defaultValues: Partial<PaymentMethod>;
  onClose: () => void;
  refreshData: () => void;
}

export const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({
  open,
  title,
  data,
  defaultValues,
  onClose,
  refreshData,
}) => {
  const classes = useStyles();
  const providerRef = React.useRef<any>(null);
  const [alert, setAlert] = useState({ show: false, type: 'success' as 'success' | 'error', text: '' });
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<string | null>(null);

  const {
    handleSubmit,
    control,
    errors,
    reset,
    watch,
    setValue,
  } = useForm<any>({ defaultValues });

  const terminalType = watch('terminalType');
  const paymentMethods = watch('paymentMethods') || defaultValues.paymentMethods;

  useEffect(() => {
    if (open) {
      reset(data || defaultValues);
    }
  }, [open, data, defaultValues, reset]);

  // Manejar cambios en el tipo de terminal
  useEffect(() => {
    if (terminalType === 'ecommerce') {
      setValue('quantity', undefined);
    } else if (terminalType === 'physical' && !watch('quantity')) {
      setValue('quantity', 1);
    }
  }, [terminalType, setValue, watch]);

  // Validación: al menos un método de pago debe estar activo
  const validatePaymentMethods = (methods: any) => {
    const activeCount = Object.values(methods || {}).filter(Boolean).length;
    return activeCount > 0 || 'Debe habilitar al menos un método de pago';
  };

  // Manejar cambio en switches de métodos de pago
  const handlePaymentMethodToggle = (methodKey: string, currentValue: boolean) => {
    const activeMethodsCount = Object.values(paymentMethods || {}).filter(Boolean).length;

    // Si es el último método activo y se intenta desactivar
    if (currentValue && activeMethodsCount === 1) {
      setPendingToggle(methodKey);
      setShowWarningModal(true);
      return;
    }

    // Permitir el cambio
    setValue(`paymentMethods.${methodKey}`, !currentValue, { shouldValidate: true });
  };

  const handleWarningClose = () => {
    setShowWarningModal(false);
    setPendingToggle(null);
  };

  const onSubmit = async (formData: any) => {
    try {
      const payload = {
        ...formData,
        id: data?.id,
      };

      let res;
      if (data?.id) {
        res = await updatePaymentMethodService(payload);
      } else {
        res = await createPaymentMethodService(payload);
      }

      if (res?.status === 200 || res?.status === 201) {
        providerRef.current?.enqueueSnackbar(
          data?.id
            ? 'Método de pago actualizado exitosamente'
            : 'Método de pago creado exitosamente',
          {
            variant: 'success',
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
          }
        );
        refreshData();
        onClose();
      } else {
        setAlert({
          show: true,
          type: 'error',
          text: 'Ha ocurrido un error, intente nuevamente.',
        });
      }
    } catch (error) {
      console.error('Error saving payment method:', error);
      setAlert({
        show: true,
        type: 'error',
        text: 'Ha ocurrido un error, intente nuevamente.',
      });
    }
  };

  const validatePrice = (value: number) => {
    if (value < 0) return 'El precio no puede ser negativo';
    if (value.toString().split('.')[0]?.length > 6) {
      return 'Solo se aceptan 6 dígitos enteros';
    }
    if (value.toString().split('.')[1]?.length > 2) {
      return 'Solo se aceptan 2 decimales';
    }
    return true;
  };

  const validateQuantity = (value: number) => {
    if (terminalType === 'physical') {
      if (!value || value <= 0) return 'La cantidad debe ser mayor a 0';
      if (value % 1 !== 0) return 'La cantidad debe ser un número entero';
    }
    return true;
  };

  const chargeTypeOptions = [
    { value: 'rental', label: 'Alquiler' },
    { value: 'one-time', label: 'Pago único' },
  ];

  const terminalTypeOptions = [
    { value: 'ecommerce', label: 'Ecommerce' },
    { value: 'physical', label: 'Dispositivo físico' },
  ];

  // Validar que tenemos datos válidos antes de renderizar
  if (!control) {
    return null;
  }

  return (
    <>
      <SnackbarProvider ref={providerRef} maxSnack={3}>
        <ActionPopUp 
          title={title} 
          open={open} 
          onClose={onClose}
          onSave={handleSubmit(onSubmit)}
          enabledControls={true}
        >
          <Box>
            <Grid container spacing={2}>
              {/* Tipo de terminal */}
              <Grid item xs={12}>
                <CustomSelect
                  name="terminalType"
                  control={control}
                  label="Tipo de terminal"
                  options={terminalTypeOptions}
                  error={errors}
                  rule={{ required: 'El tipo de terminal es requerido' }}
                />
              </Grid>

              {/* Nombre */}
              <Grid item xs={12}>
                <CustomInput
                  name="name"
                  control={control}
                  label={terminalType === 'ecommerce' ? 'Nombre del canal' : 'Nombre del producto'}
                  error={errors}
                  rule={{
                    required: 'El nombre es requerido',
                    validate: (value: string) => {
                      if (/\s\s/.test(value)) {
                        return 'No puede contener más de 2 espacios consecutivos';
                      }
                      return true;
                    },
                  }}
                />
              </Grid>

              {/* Descripción */}
              <Grid item xs={12}>
                <CustomInput
                  name="description"
                  control={control}
                  label="Descripción"
                  error={errors}
                  rule={{ required: 'La descripción es requerida' }}
                  multiline
                  rows={3}
                />
              </Grid>

              {/* Precio */}
              <Grid item xs={terminalType === 'physical' ? 6 : 12}>
                <CustomInput
                  name="price"
                  control={control}
                  label="Precio"
                  error={errors}
                  rule={{
                    required: 'El precio es requerido',
                    validate: validatePrice,
                  }}
                  type="number"
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>

              {/* Cantidad - Solo para dispositivo físico */}
              {terminalType === 'physical' && (
                <Grid item xs={6}>
                  <CustomInput
                    name="quantity"
                    control={control}
                    label="Cantidad"
                    error={errors}
                    rule={{
                      required: 'La cantidad es requerida',
                      validate: validateQuantity,
                    }}
                    type="number"
                    inputProps={{ step: '1', min: '1' }}
                  />
                </Grid>
              )}

              {/* Tipo de cobro */}
              <Grid item xs={12}>
                <CustomSelect
                  name="chargeType"
                  control={control}
                  label="Tipo de cobro"
                  options={chargeTypeOptions}
                  error={errors}
                  rule={{ required: 'El tipo de cobro es requerido' }}
                />
              </Grid>

              {/* Estado */}
              <Grid item xs={12}>
                <Controller
                  name="active"
                  control={control}
                  render={(props) => (
                    <FormControlLabel
                      control={
                        <CustomSwitch
                          check={props.value}
                          handleChange={(e: any) => props.onChange(e.target.checked)}
                          checked={props.value}
                          lblOff=""
                          lblOn=""
                        />
                      }
                      label={props.value ? 'Activo' : 'Inactivo'}
                    />
                  )}
                />
              </Grid>

              {/* Métodos de pago */}
              <Grid item xs={12}>
                <Box className={classes.switchContainer}>
                  <Typography variant="subtitle2" className={classes.switchLabel}>
                    Métodos de pago disponibles
                  </Typography>

                  <Controller
                    name="paymentMethods.card"
                    control={control}
                    rules={{ validate: () => validatePaymentMethods(paymentMethods) }}
                    render={(props) => (
                      <FormControlLabel
                        control={
                          <CustomSwitch
                            check={props.value}
                            handleChange={() => handlePaymentMethodToggle('card', props.value)}
                            checked={props.value}
                            lblOff=""
                            lblOn=""
                          />
                        }
                        label="Tarjeta"
                      />
                    )}
                  />

                  <Controller
                    name="paymentMethods.paymentButton"
                    control={control}
                    render={(props) => (
                      <FormControlLabel
                        control={
                          <CustomSwitch
                            check={props.value}
                            handleChange={() => handlePaymentMethodToggle('paymentButton', props.value)}
                            checked={props.value}
                            lblOff=""
                            lblOn=""
                          />
                        }
                        label="Botón de pago"
                      />
                    )}
                  />

                  <Controller
                    name="paymentMethods.transfer"
                    control={control}
                    render={(props) => (
                      <FormControlLabel
                        control={
                          <CustomSwitch
                            check={props.value}
                            handleChange={() => handlePaymentMethodToggle('transfer', props.value)}
                            checked={props.value}
                            lblOff=""
                            lblOn=""
                          />
                        }
                        label="Transferencia"
                      />
                    )}
                  />

                  {errors.paymentMethods && (
                    <Typography color="error" variant="caption">
                      {errors.paymentMethods.card?.message ||
                        errors.paymentMethods.paymentButton?.message ||
                        errors.paymentMethods.transfer?.message}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </ActionPopUp>

        <Toaster
          show={alert.show}
          type={alert.type}
          text={alert.text}
          onClose={() => setAlert({ show: false, type: 'success', text: '' })}
        />
      </SnackbarProvider>

      {/* Modal de advertencia - Último método activo */}
      <Dialog
        open={showWarningModal}
        onClose={handleWarningClose}
        className={classes.modalWarning}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            Debe habilitar al menos un método de pago
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Para continuar, es necesario que exista al menos un método de pago activo en la
            configuración del cobro de la solicitud de link de pagos. Por favor, habilite un
            método de pago antes de guardar los cambios.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWarningClose} color="primary" variant="contained">
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaymentMethodForm;
